<template>
    <content-page
        title="Εγγραφή ομάδας"
        subtitle="Συμπλήρωσε τα στοιχεία της ομάδας και των παικτών. Θα επικοινωνήσουμε μαζί σου για την επιβεβαίωση."
        icon="mdi-account-plus-outline"
        wide
    >
        <v-form ref="form" v-model="valid" class="register-form">
            <section class="register-section">
                <div class="register-step">
                    <span class="register-step__number custom-orange">1</span>
                    <span class="register-step__title">Στοιχεία ομάδας</span>
                </div>
                <v-row>
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model="register.teamName"
                            color="orange"
                            label="Όνομα ομάδας"
                            outlined
                            prepend-inner-icon="mdi-account-group"
                            :rules="[rules.required]"
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model="register.telephone"
                            type="tel"
                            maxlength="10"
                            color="orange"
                            label="Τηλέφωνο ομάδας"
                            outlined
                            prepend-inner-icon="mdi-phone"
                            :rules="[rules.required, rules.phone]"
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model="register.email"
                            type="email"
                            color="orange"
                            label="Email ομάδας"
                            outlined
                            prepend-inner-icon="mdi-email-outline"
                            :rules="[rules.required, rules.email]"
                        />
                    </v-col>
                </v-row>
            </section>

            <section class="register-section">
                <div class="register-step">
                    <span class="register-step__number custom-orange">2</span>
                    <span class="register-step__title">Παίκτες</span>
                    <span class="register-step__count">{{ register.players.length }}</span>
                </div>

                <div v-for="(player, i) in register.players" :key="player.key" class="player-card">
                    <div class="player-card__header">
                        <span class="player-card__index">{{ i + 1 }}</span>
                        <span class="player-card__name">
                            {{ player.name || player.surname ? `${player.name || ''} ${player.surname || ''}` : 'Νέος παίκτης' }}
                        </span>
                        <v-btn icon small color="red" title="Διαγραφή παίκτη" @click="removePlayer(i)">
                            <v-icon>mdi-trash-can-outline</v-icon>
                        </v-btn>
                    </div>
                    <v-row dense>
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field v-model="player.name" color="orange" label="Όνομα" outlined dense :rules="[rules.required]" />
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field v-model="player.surname" color="orange" label="Επώνυμο" outlined dense :rules="[rules.required]" />
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field v-model="player.birthday" type="date" color="orange" label="Ημερομηνία γέννησης" outlined dense />
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field
                                v-model="player.mobile"
                                type="tel"
                                maxlength="10"
                                color="orange"
                                label="Κινητό"
                                outlined
                                dense
                                :rules="[rules.mobile]"
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field
                                v-model="player.email"
                                type="email"
                                color="orange"
                                label="Email"
                                outlined
                                dense
                                :rules="[rules.email]"
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field v-model="player.residence" color="orange" label="Κατοικία" outlined dense />
                        </v-col>
                        <v-col cols="6" sm="3" md="4">
                            <v-select
                                v-model="player.position"
                                :items="positions"
                                color="orange"
                                item-color="orange"
                                label="Θέση"
                                outlined
                                dense
                                clearable
                            />
                        </v-col>
                        <v-col cols="6" sm="3" md="4">
                            <v-text-field v-model="player.jersey" type="number" color="orange" label="Αριθμός φανέλας" outlined dense />
                        </v-col>
                        <v-col cols="6" sm="3" md="4">
                            <v-text-field v-model="player.height" color="orange" label="Ύψος" suffix="cm" outlined dense />
                        </v-col>
                    </v-row>
                </div>

                <button type="button" class="add-player" @click="addPlayer">
                    <v-icon color="orange darken-2">mdi-plus-circle-outline</v-icon>
                    Προσθήκη παίκτη
                </button>
            </section>

            <section class="register-submit">
                <v-checkbox v-model="terms" color="orange" hide-details class="mt-0">
                    <template v-slot:label>
                        <span class="register-terms">
                            Αποδέχομαι τον
                            <router-link :to="{ name: 'Regulations' }" target="_blank" @click.native.stop>κανονισμό του τουρνουά</router-link>
                        </span>
                    </template>
                </v-checkbox>
                <v-btn
                    :disabled="!terms || !valid"
                    :loading="submitting"
                    large
                    depressed
                    class="custom-orange white--text register-submit__btn"
                    @click="registerTeam()"
                >
                    Ολοκλήρωση εγγραφής
                </v-btn>
            </section>
        </v-form>
        <custom-dialog :dialog="showDialog" :title="title" :message="message" @update="closeDialog" />
    </content-page>
</template>

<script>
    import teamService from '@/services/team'
    import CustomDialog from '@/components/Dialog'
    import ContentPage from '@/components/ContentPage'

    let playerKey = 0

    function emptyPlayer() {
        playerKey += 1
        return {
            key: playerKey,
            name: null,
            surname: null,
            residence: null,
            mobile: null,
            email: null,
            height: null,
            jersey: null,
            position: null,
            birthday: null
        }
    }

    function emptyRegister() {
        return { teamName: null, telephone: null, teamLogo: null, email: null, players: [] }
    }

    export default {
        components: { CustomDialog, ContentPage },
        data() {
            return {
                register: emptyRegister(),
                terms: false,
                valid: false,
                submitting: false,
                showDialog: false,
                message: '',
                title: '',
                positions: [
                    { text: 'Guard', value: 'guard' },
                    { text: 'Forward', value: 'forward' },
                    { text: 'Center', value: 'center' }
                ],
                rules: {
                    required: value => !!value || 'Υποχρεωτικό πεδίο',
                    email: value => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'Μη έγκυρο email',
                    // Greek landline (2xxxxxxxxx) or mobile (69xxxxxxxx)
                    phone: value => !value || /^(2\d{9}|69\d{8})$/.test(value) || 'Μη έγκυρο τηλέφωνο (10 ψηφία)',
                    mobile: value => !value || /^69\d{8}$/.test(value) || 'Μη έγκυρο κινητό (69xxxxxxxx)'
                }
            }
        },
        methods: {
            addPlayer() {
                this.register.players.push(emptyPlayer())
            },
            removePlayer(index) {
                this.register.players.splice(index, 1)
            },
            registerTeam() {
                if (!this.$refs.form.validate()) {
                    return
                }
                const payload = {
                    ...this.register,
                    players: this.register.players.map(({ key, ...player }) => player)
                }
                this.submitting = true
                teamService.register(payload).then(() => {
                    this.showDialog = true
                    this.message = 'Καλή τύχη στο νέο πρωτάθλημα!'
                    this.title = 'Η εγγραφή σας ήταν επιτυχής!'
                    this.register = emptyRegister()
                    this.terms = false
                    this.$refs.form.resetValidation()
                }).catch(() => {
                    this.showDialog = true
                    this.message = 'Υπάρχει ήδη αίτηση με αυτό το όνομα ομάδας. Αν το πρόβλημα συνεχίζεται, επικοινώνησε μαζί μας.'
                    this.title = 'Η εγγραφή σας ήταν ανεπιτυχής!'
                }).finally(() => {
                    this.submitting = false
                })
            },
            closeDialog() {
                this.showDialog = false
                this.message = ''
                this.title = ''
            }
        }
    }
</script>

<style scoped>
.register-section {
    margin-bottom: 28px;
}

.register-step {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
}

.register-step__number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    color: #fff;
    font-size: 0.85rem;
    font-weight: 800;
}

.register-step__title {
    font-size: 1.2rem;
    font-weight: 800;
    font-family: Mansalva, Arial, sans-serif;
}

.register-step__count {
    padding: 0 8px;
    border-radius: 10px;
    background: #0d0d0f;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 700;
}

/* ---------- Player card ---------- */
.player-card {
    margin-bottom: 14px;
    padding: 12px 14px 4px;
    border-radius: 10px;
    background: #f7f7f8;
    border-left: 4px solid #ff7133;
}

.player-card__header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.player-card__index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 26px;
    height: 24px;
    border-radius: 6px;
    background: #0d0d0f;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 700;
}

.player-card__name {
    flex: 1 1 auto;
    font-weight: 700;
}

.player-card ::v-deep .v-input__slot {
    background: #fff !important;
}

.add-player {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 14px;
    border: 2px dashed rgba(255, 113, 51, 0.6);
    border-radius: 10px;
    background: rgba(255, 113, 51, 0.04);
    color: #e65100;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s ease;
}

.add-player:hover {
    background: rgba(255, 113, 51, 0.1);
}

/* ---------- Submit ---------- */
.register-submit {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
    padding-top: 20px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.register-terms {
    color: rgba(0, 0, 0, 0.85);
    font-weight: 600;
}

.register-terms a {
    color: #ef4444;
}

.register-submit__btn {
    font-weight: 700;
    text-transform: none;
    letter-spacing: normal;
}

@media (max-width: 600px) {
    .register-submit__btn {
        width: 100%;
    }
}
</style>
